import { firstValueFrom, Subject, take, takeUntil } from 'rxjs';
import { v4 } from 'uuid';
import type { RecordResult } from '../../../@types/records';
import { generateDbRecordId, isDbRecordId } from '../../../db/odin-db-util';
import type { SyncDetails } from '../../../redux/middleware/sync-transforms';
import { dbApi } from '../../../redux/services/db-api';
import { recordApi } from '../../../redux/services/record';
import { submitSaveRecordJob, submitUpdateRecordJob } from '../../../redux/slices/background-job-slice';
import { appDispatch } from '../../../redux/store-accessors';
import { saveRecordData, saveRecordSuccessCallbackMap } from '../../../services/background-jobs/save-record.handler';
import {
  updateRecordData,
  updateRecordSuccessCallbackMap,
} from '../../../services/background-jobs/update-record.handler';
import { onlineStatusService } from '../../../services/online-status.service';
import { RecordSaveState } from '../record.component';
import { isWebView } from '../../../redux/util/webview-base-query';

export const BACKGROUND_SAVE_KEY = 'save_method_frontend_background';

/**
 * Mutates a record by either updating an existing record or creating a new one.
 * Supports both background job processing and direct API calls based on configuration.
 *
 * @param record - The record data to be mutated
 * @param details - Configuration object containing mutation parameters
 * @param details.recordId - Optional record ID for offline record handling
 * @param details.isBackgroundSaveEnabled - Whether to process the mutation as a background job
 * @param details.recordSaveState - The current save state of the record (Update or Create)
 * @param details.destroySubject - RxJS subject for handling component destruction/cleanup
 * @param details.formCaption - Optional caption text for the form/job display
 * @param details.docNo - Optional document number for job display
 * @param details.parentLinkedRecordId - Optional parent record ID for linked records
 * @param details.stepAdvanced - Optional flag indicating if advanced steps should be applied
 * @param details.syncDetails - Optional additional details related to synchronization
 *
 * @returns Promise that resolves to the mutated record ID or record data
 *
 * @throws Will throw an error if the mutation operation fails
 */
export const mutateRecord = async (
  record: RecordResult,
  details: {
    recordId?: string | number; // needed to ensure offline records are saved correctly
    isBackgroundSaveEnabled: boolean;
    recordSaveState: RecordSaveState;
    destroySubject: Subject<void>;
    formCaption?: string;
    docNo?: string;
    parentLinkedRecordId?: number;
    stepAdvanced?: boolean;
    syncDetails?: SyncDetails;
  }
) => {
  const { recordSaveState, isBackgroundSaveEnabled, destroySubject } = details;

  // The mobile app does not support background jobs
  const shouldUseBackgroundSave = isBackgroundSaveEnabled && !isWebView();

  if (shouldUseBackgroundSave) {
    const jobId = v4();
    const recordMutationSubject = new Subject<number>();

    if (recordSaveState === RecordSaveState.Update && record.id) {
      updateRecordSuccessCallbackMap.set(jobId, recordMutationSubject);
      updateRecordData.set(jobId, record);

      appDispatch(
        submitUpdateRecordJob({
          jobId,
          details: { jobItemText: `${details?.formCaption} - ${details?.docNo}`, stepAdvanced: details?.stepAdvanced },
        })
      );

      return firstValueFrom(recordMutationSubject.asObservable().pipe(take(1), takeUntil(destroySubject)));
    } else {
      saveRecordSuccessCallbackMap.set(jobId, recordMutationSubject);
      saveRecordData.set(jobId, record);

      appDispatch(submitSaveRecordJob({ jobId, details: { jobItemText: details?.formCaption } }));

      return firstValueFrom(recordMutationSubject.asObservable().pipe(take(1), takeUntil(destroySubject)));
    }
  } else {
    const offline = onlineStatusService.getStatus() === false;
    if (recordSaveState === RecordSaveState.Update && record.id) {
      const shouldUpdateDbRecordMutation = details.recordId ? isDbRecordId(details.recordId) : false;
      if (shouldUpdateDbRecordMutation || offline) {
        let dbId = record.id;
        if (!isDbRecordId(record.id)) {
          // convert server record ID to negative value to create corresponding local DB ID
          // (negative IDs distinguish offline/local records from server-synced records)
          dbId = -record.id;
        }
        return appDispatch(
          dbApi.endpoints.updateDbRecord.initiate({
            id: dbId,
            record,
            syncDetails: details.syncDetails,
          })
        ).unwrap();
      } else {
        return appDispatch(
          recordApi.endpoints.updateRecord.initiate({
            id: record.id,
            patch: record,
            applyDefaultValues: false,
            stepAdvanced: details?.stepAdvanced,
          })
        ).unwrap();
      }
    } else {
      if (offline) {
        return appDispatch(
          dbApi.endpoints.addDbRecord.initiate({
            record: { id: generateDbRecordId(), ...record },
          })
        ).unwrap();
      } else {
        return appDispatch(
          recordApi.endpoints.addRecord.initiate({
            body: record,
            applyDefaultValues: false,
            parentRecordId: details?.parentLinkedRecordId,
          })
        ).unwrap();
      }
    }
  }
};
